import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusIndicatorBadge from '../../../components/ui/StatusIndicatorBadge';

const RegistrationTable = ({ 
  registrations, 
  onViewDetails, 
  userRole,
  pagination = { page: 1, pageSize: 10, total: 0, totalPages: 1 },
  onPageChange
}) => {

  const { page: currentPage, pageSize, total, totalPages } = pagination;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Registration List ({total || registrations?.length || 0})
        </h3>
      </div>
      
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">School Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">City</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Age Category</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Coach Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Submitted Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Reviewer</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations?.map((registration) => (
              <tr key={registration?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-foreground">{registration?.schoolName}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{registration?.city}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{registration?.ageCategory}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{registration?.coachName}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{registration?.submittedDate}</td>
                <td className="px-4 py-3">
                  <StatusIndicatorBadge status={registration?.status} />
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{registration?.reviewer || 'Unassigned'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onViewDetails(registration?.id)}
                      className="p-1 hover:bg-muted rounded transition-colors touch-target"
                      aria-label="View details"
                    >
                      <Icon name="Eye" size={18} color="var(--color-primary)" />
                    </button>
                    {userRole === 'super_admin' && (
                      <button
                        onClick={() => onViewDetails(registration?.id)}
                        className="p-1 hover:bg-muted rounded transition-colors touch-target"
                        aria-label="Edit registration"
                      >
                        <Icon name="Edit" size={18} color="var(--color-secondary)" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {registrations?.map((registration) => (
          <div key={registration?.id} className="border border-border rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3 bg-card">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground truncate">{registration?.schoolName}</h4>
                <p className="text-xs text-muted-foreground truncate">{registration?.city}</p>
              </div>
              <StatusIndicatorBadge status={registration?.status} className="flex-shrink-0" />
            </div>
            
            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span className="text-muted-foreground">Age Category:</span>
                <p className="font-medium text-foreground">{registration?.ageCategory}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Coach:</span>
                <p className="font-medium text-foreground truncate">{registration?.coachName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Submitted:</span>
                <p className="font-medium text-foreground">{registration?.submittedDate}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Reviewer:</span>
                <p className="font-medium text-foreground">{registration?.reviewer || 'Unassigned'}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <button
                onClick={() => onViewDetails(registration?.id)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium touch-target"
              >
                <Icon name="Eye" size={18} />
                View Details
              </button>
              {userRole === 'super_admin' && (
                <button
                  onClick={() => onViewDetails(registration?.id)}
                  className="p-2 border border-border rounded-md hover:bg-muted transition-colors touch-target"
                  aria-label="Edit registration"
                >
                  <Icon name="Edit" size={18} color="var(--color-secondary)" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Showing {startIndex + 1} to {endIndex} of {total} entries
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange && onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </Button>
            <span className="text-sm text-foreground px-2 sm:px-3">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange && onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
              iconPosition="right"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationTable;